/* eslint-disable no-console */
/* eslint-disable camelcase */
/* global OktaSignIn */
import { loadAndSyncSession } from '../scripts/login-frame';
import  { nanoid }  from 'nanoid';
import { getOAuthState, setLoginVerificationValues } from '../cookie-utils';
import sessionStorageUtil from '../session-storage-util';
import config from '../config';
import { ValidationError } from '../errors';

const DEVICE_TIME = Date.now();
const LOGIN_CONTAINER_ID = 'okta-login-container';
const LOGIN_CONTAINER_SELECTOR = '#okta-login-container';
const ATHENA_OAUTH_STATE_COOKIE = 'athena-oauth-state';

function treatAsJSON(value) {
  return value ? JSON.parse(value) : undefined;
}

export function getLoginContainerData() {
  const loginContainer = document.getElementById(LOGIN_CONTAINER_ID);

  const containerData = {
    oktaUrl: loginContainer.getAttribute('data-okta-url'),
    consentOktaUrl: loginContainer.getAttribute('data-consent-okta-url'),
    athenanet: treatAsJSON(loginContainer.getAttribute('data-athenanet')),
    authorizeRedirectUrl: loginContainer.getAttribute('data-authorize-redirect-url'),
    proxiedAuthorizeUrl: loginContainer.getAttribute('data-proxied-authorize-url'),
    proxiedAuthorizeValues: treatAsJSON(loginContainer.getAttribute('data-proxied-authorize-values')),
    forgotPasswordAthena: loginContainer.getAttribute('data-forgot-password-athena'),
    supportEnv: loginContainer.getAttribute('data-support-env'),
    anetUserDomain: loginContainer.getAttribute('data-anet-user-domain'),
    createAccount: treatAsJSON(loginContainer.getAttribute('data-create-account')),
    prompt: loginContainer.getAttribute('data-prompt'),
    username: loginContainer.getAttribute('data-username'),
    sessionToken: loginContainer.getAttribute('data-session-token'),
    stateToken: loginContainer.getAttribute('data-state-token'),
    applicationId: loginContainer.getAttribute('data-application-id'),
    redirectUri: loginContainer.getAttribute('data-redirect-uri'),
    accessToken: loginContainer.getAttribute('data-access-token'),
    idToken: loginContainer.getAttribute('data-id-token'),
    logo: loginContainer.getAttribute('data-logo-url'),
    logoText: loginContainer.getAttribute('data-logo-text'),
    defaultPostAuthnRoutes: treatAsJSON(loginContainer.getAttribute('data-post-authn-routes')),
    iamCoreUrl: loginContainer.getAttribute('data-iam-core-url'),
    helpLink: loginContainer.getAttribute('data-help-link'),
    customLinks: loginContainer.getAttribute('data-custom-links'),
    customLinksAthena: treatAsJSON(loginContainer.getAttribute('data-custom-links-athena')),
    i18n: JSON.parse(loginContainer.getAttribute('data-i18n')),
    displayName: loginContainer.getAttribute('data-display-name'),
    language: loginContainer.getAttribute('data-language'),
    scope: loginContainer.getAttribute('data-scope'),
    userState: loginContainer.getAttribute('data-user-state'),
    sanitizeInfo: loginContainer.getAttribute('data-sanitize-info'),
    amplitudeApiKey: loginContainer.getAttribute('data-amplitude-api-key'),
    serverTime: loginContainer.getAttribute('data-server-time'),
    deviceTime: DEVICE_TIME,
    testApp: loginContainer.getAttribute('data-test-app'),
    workflow: loginContainer.getAttribute('data-workflow'),
    idp: loginContainer.getAttribute('data-idp'),
    features: treatAsJSON(loginContainer.getAttribute('data-features')),
    workflowType: loginContainer.getAttribute('data-workflow-type'),
    newUser: treatAsJSON(loginContainer.getAttribute('data-new-user')),
    lwaUniqId: loginContainer.getAttribute('data-lwa-uniq-id'),
    deviceId: loginContainer.getAttribute('data-device-id'),
    amplitudeUserId: loginContainer.getAttribute('data-amplitude-user-id'),
    amplitudeDeviceId: loginContainer.getAttribute('data-amplitude-device-id'),
    relogin: loginContainer.getAttribute('data-relogin'),
    workflowHandlerEndpoint: loginContainer.getAttribute('data-workflow-handler-endpoint'),
    aud: loginContainer.getAttribute('data-aud'),
    state: loginContainer.getAttribute('data-state'),
    emailToken: loginContainer.getAttribute('data-email-token'),
    hideResetAccountOption: treatAsJSON(loginContainer.getAttribute('data-hide-reset-account-option')),
    appClientId: loginContainer.getAttribute('data-app-clientid'),
    loginFrameUrl: loginContainer.getAttribute('data-iframe-url'),
    showTermsText: treatAsJSON(loginContainer.getAttribute('data-show-terms-text')),
    showTermsTextOnLogin: treatAsJSON(loginContainer.getAttribute('data-show-terms-text-on-login')),
    userAppTypeMismatchError: loginContainer.getAttribute('data-user-app-type-mismatch-error'),
    emailTokenLoggedInUserMismatchError: loginContainer.getAttribute('data-email-token-logged-in-user-mismatch-error'),
    userNamespace: loginContainer.getAttribute('data-user-namespace'),
    manageProfileError: treatAsJSON(loginContainer.getAttribute('data-manage-profile-error')),
    oktaOrgId: loginContainer.getAttribute('data-okta-org-id'),
    emailTokenUserRegisteredWarning: loginContainer.getAttribute('data-email-token-user-registered-warning'),
    showSetPasswordDescription: treatAsJSON(loginContainer.getAttribute('data-show-set-password-description')),
  };
  return containerData;
}

const transformUsername = function (options, username, operation) {
  let { userNamespace } = options;
  // Ensure we don't end up appending `undefined` to username,
  // and it's expected to be set only for patient fuzzy users
  userNamespace = userNamespace || '';
  if (userNamespace) {
    userNamespace = username.endsWith(userNamespace) ? '' : userNamespace;
  }
  return username.indexOf('@') > -1
    ? `${username}${userNamespace}`
    : username + '@' + options.anetUserDomain;
};

export function createWidget(options) {

  const urlParams = new URLSearchParams(window.location.search);
  return new OktaSignIn({
    anetUserDomain: options.anetUserDomain,
    athenanet: options.athenanet,
    baseUrl: options.oktaUrl,
    consentOktaUrl: options.consentOktaUrl,
    supportEnv: options.supportEnv,
    username: options.username,
    applicationId: options.applicationId,
    redirectUri: options.redirectUri,
    stateToken: options.stateToken,
    helpLinks: {
      forgotPasswordAthena: options.forgotPasswordAthena,
      help: options.helpLink,
      custom: options.customLinks,
      customAthena: options.customLinksAthena,
    },
    language: options.language,
    logo: options.logo,
    logoText: options.logoText,
    iamCoreUrl: options.iamCoreUrl,
    createAccount: options.createAccount,
    defaultPostAuthnRoutes: options.defaultPostAuthnRoutes,
    passwordReset: urlParams.get('reset') === '1' ? true : false,
    transformUsername: transformUsername.bind(undefined, options),
    i18n: options.i18n,
    displayName: options.displayName,
    scope: options.scope,
    userState: options.userState,
    sanitizeInfo: options.sanitizeInfo,
    amplitudeApiKey: options.amplitudeApiKey,
    testApp: options.testApp,
    workflow: options.workflow,
    idp: options.idp,
    features: options.features,
    workflowType: options.workflowType,
    newUser: options.newUser,
    lwaUniqId: options.lwaUniqId,
    proxiedAuthorizeUrl: options.proxiedAuthorizeUrl,
    proxiedAuthorizeValues: options.proxiedAuthorizeValues,
    amplitudeUserId: options.amplitudeUserId,
    amplitudeDeviceId: options.amplitudeDeviceId,
    deviceId: options.deviceId,
    relogin: options.relogin,
    workflowHandlerEndpoint: options.workflowHandlerEndpoint,
    aud: options.aud,
    state: options.state,
    emailToken: options.emailToken,
    hideResetAccountOption: options.hideResetAccountOption,
    appClientId: options.appClientId,
    showTermsText: options.showTermsText,
    showTermsTextOnLogin: options.showTermsTextOnLogin,
    userAppTypeMismatchError: options.userAppTypeMismatchError,
    emailTokenLoggedInUserMismatchError: options.emailTokenLoggedInUserMismatchError,
    manageProfileError: options.manageProfileError,
    oktaOrgId: options.oktaOrgId,
    showSetPasswordDescription: options.showSetPasswordDescription,
    emailTokenUserRegisteredWarning: options.emailTokenUserRegisteredWarning,
  });
}

/**
 * Determines whether the user should be allowed to access their Okta profile.
 * Currently only used to disallow athenaNet users from accessing their okta profiles.
 *
 * @param {string} username Okta login username (email address)
 * @returns {boolean} whether the user should be allowed profile access
 */
function allowProfileAccess(user, anetUserDomain) {
  try {
    return !user.profile.login.match('@' + anetUserDomain);
  } catch (e) {
    // Profile access should be enabled by default
    return true;
  }
}

/**
 * decode JWT client-side
 */
function decodeJWT(rawToken) {
  const token = {};
  token.header = JSON.parse(window.atob(rawToken.split('.')[0]));
  token.payload = JSON.parse(window.atob(rawToken.split('.')[1]));
  return token;
}
/**
 * Given the server time, figure out based on the local device time how much difference there is.
 * Intended so that local checking of things like the access token expire time will still work if the device's
 * clock differs from the server clock.
 *
 * Logic: If the server time is S, and the token expires at S+30, but the device thinks it's already S+50, then set the
 * expire time to be S+80 (e.g. the offset would be 50 = localTime - serverTime)
 */
function getDeviceTimeOffset({ serverTime, deviceTime }) {
  const deviceTimeOffset = serverTime ? Math.round((deviceTime - parseInt(serverTime)) / 1000) : 0;
  return deviceTimeOffset;
}

/**
 * Format the access token the way the token manager in the OSW expects
 */
function formatAccessToken({ accessToken, deviceTimeOffset }) {
  const token = decodeJWT(accessToken);
  return {
    accessToken: accessToken,
    expiresAt: token.payload.exp + deviceTimeOffset,
    tokenType: 'Bearer',
    scopes: token.payload.scp,
    authorizeUrl: token.payload.iss + '/v1/authorize',
    userinfoUrl: token.payload.iss + '/v1/userinfo',
  };
}

/**
 * Format the id token the way the token manager in the OSW expects
 *
 * The scopes parameter is kind of weird and perhaps unnecessary, but
 * it's set in the OSW, so including to match it in case something in
 * their codebase expects it
 */
function formatIdToken({ idToken, scopes, deviceTimeOffset }) {
  const token = decodeJWT(idToken);
  return {
    idToken: idToken,
    claims: token.payload,
    expiresAt: token.payload.exp + deviceTimeOffset,
    scopes: scopes,
    authorizeUrl: token.payload.iss + '/v1/authorize',
    issuer: token.payload.iss,
    clientId: token.payload.aud,
  };
}
function showPrimaryAuth() {
  const currentHash = window.location.hash.replace(/^#/, '');
  // Some pages shouldn't go to the login page.
  const noLogins = [
    'create',
    'profile',
    'signin/athena-forgot-password',
  ];
  document.getElementById(LOGIN_CONTAINER_ID).style.display = 'block';
  if (noLogins.indexOf(currentHash) >= 0 ) {
    return;
  }
  if (currentHash !== '') {
    window.location.hash = '';
  }
}

function isUserMismatchError(containerData) {
  return containerData.userAppTypeMismatchError || containerData.emailTokenLoggedInUserMismatchError;
}

export function renderLogin(oktaSignIn, authorizeRedirectUrl, accessToken, forceLogin, oktaAuth) { // eslint-disable-line max-params
  const containerData = getLoginContainerData();
  const deviceTimeOffset = getDeviceTimeOffset({
    serverTime: containerData.serverTime,
    deviceTime: containerData.deviceTime,
  });
  if (containerData.accessToken) {
    accessToken = formatAccessToken({ accessToken: containerData.accessToken, deviceTimeOffset });
    oktaSignIn.authClient.tokenManager.add('access_token', accessToken);
  }
  if (containerData.idToken) {
    oktaSignIn.authClient.tokenManager.add(
      'id',
      formatIdToken({ idToken: containerData.idToken, scopes: accessToken.scopes, deviceTimeOffset })
    );
  }
  // display the waiting route so in case there's a session, we don't flash the login
  // screen before going to the next post authn route.  Instead, we'll just see a spinner
  let initialPageLoaded = false;
  //Show spinner while AOSW renders
  if (containerData.accessToken) {
    window.location.hash = 'waiting';
  } else {
    document.getElementById(LOGIN_CONTAINER_ID).style.display = 'none';
  }
  oktaSignIn.on('pageRendered', function () {
    if (forceLogin) {
      showPrimaryAuth(oktaSignIn);
    } else if (!initialPageLoaded) {
      oktaSignIn.authClient.session.exists()
        .then (async (exists) => {
          // Avoid redirecting to login_callback when session exists and an error has
          // occurred in pre/post-authn phase so that the error UI doesn't get removed
          if (isUserMismatchError(containerData)) {
            showPrimaryAuth(oktaSignIn);
          } else if (exists || containerData.accessToken) {
            // If an accessToken was passed in, it means we just successfully went through the auth code flow,
            // and there is definitely a session. If the session check declares that there is
            // no session, it means session checking is unreliable, and we should mark it as
            // such so that all subsequent session checking will be disabled
            if (!exists) {
              oktaSignIn.router.settings.set('sessionCheckingUnreliable', true);
              oktaSignIn.router.appState.trigger('beginPostAuthnRoutes');
            } else if (!containerData.accessToken && !accessToken) {
              // We have a session, but no access token. Send user to endpoint which will redirect to okta /authorize
              authorizeToOkta(containerData.proxiedAuthorizeValues, oktaAuth);
            } else {
              oktaSignIn.router.settings.set('sessionCheckingUnreliable', true);
              oktaSignIn.router.appState.trigger('beginPostAuthnRoutes');
              const { oktaUrl, loginFrameUrl } = containerData;
              if (loginFrameUrl) {
                await loadAndSyncSession({ loginFrameUrl, oktaUrl });
              }
            }
          } else if (containerData.idp) {
            authorizeToOkta(containerData.proxiedAuthorizeValues, oktaAuth);
          } else {
            showPrimaryAuth(oktaSignIn);
          }
        });
      initialPageLoaded = true;
    }

    const userElements = document.querySelectorAll('[name="username"]');

    userElements.forEach(userElement => {
      // Per https://stackoverflow.com/questions/35513968/disable-auto-correct-in-safari-text-input we
      // need to set all the following attributes to avoid having autocorrect on the username field
      // in Safari. (Okta already sets autocomplete="off")
      userElement.setAttribute('autocorrect', 'off');
      userElement.setAttribute('autocapitalize', 'off');
      userElement.setAttribute('spellcheck', false);
    });

  });
  oktaSignIn.renderEl(
    { el: LOGIN_CONTAINER_SELECTOR },
    (res) => {
      if (res.status === 'SUCCESS' && res.session.token) {
        const proxiedAuthorizeValues = containerData.proxiedAuthorizeValues;
        // Do not allow athenaNet users to access Okta profile
        if (allowProfileAccess(res.user, containerData.anetUserDomain)) {
          proxiedAuthorizeValues.profile = true;
        }
        // Go get the access token by initiating an OIDC flow via our endpoint that redirects to okta /authorize
        proxiedAuthorizeValues.sessionToken = res.session.token;
        authorizeToOkta(containerData.proxiedAuthorizeValues, oktaAuth);
      } else {
        window.location = `${authorizeRedirectUrl}`;
      }
    },
    (err) => {
      // Temporary error handling
      // eslint-disable-next-line no-alert
      alert(`error: ${JSON.stringify(err)}`);
    }
  );
}

const authorizeToOkta = (proxiedAuthorizeValues, oktaAuth) => {
  const values = getAuthorizeToOktaValues({
    proxiedAuthorizeValues,
  });
  setLoginVerificationValues({ state: values.cookies.oauthState, nonce: values.cookies.oauthNonce });
  const {
    idp,
    prompt,
    sessionToken,
    state,
    loginHint,
  } = values.authorizeParams;
  //Need to store this state since this gets cleared out by oka-oauth-js
  sessionStorageUtil.setLoginState(state);
  return oktaAuth.signInWithRedirect({
    idp,
    prompt,
    state,
    sessionToken,
    originalUri: window.location.href,
    loginHint,
  });
};

function getUniqueId() {
  return nanoid();
}

const getAuthorizeToOktaValues = ({ proxiedAuthorizeValues }) => {

  // https://tools.ietf.org/html/rfc6819#section-3.6
  const oauthState = getUniqueId();
  const oauthNonce = getUniqueId();

  const values = Object.assign({}, proxiedAuthorizeValues);
  const loginHint = values.login_hint;

  values.uniqueKey = oauthState;
  const sessionToken = values.sessionToken;
  delete values.profile;
  delete values.sessionToken;
  const { idp, prompt } = values;
  let finalPrompt;
  if (idp) {
    // with IDP we don't want to prompt for consent for the myidentity-webapp
    if (prompt === 'consent') {
      finalPrompt = '';
    } else {
      finalPrompt = prompt;
    }
  } else {
    // We just authenticated the user by forcing showPrimaryAuth for non-idp
    finalPrompt = 'none';
  }

  const authorizeParams = {
    response_type: 'code',
    client_id: config.oauthClientId,
    prompt: finalPrompt,
    state: JSON.stringify(values),
    nonce: oauthNonce,
    idp,
    sessionToken,
    loginHint,
  };
  return {
    authorizeParams,
    cookies: {
      oauthNonce,
      oauthState,
    },
  };
};

export const validateState = ({ parsedState }) => {
  const stateCookie = getOAuthState();
  if (!stateCookie) {
    console.warn('No cookies present in request');
    throw new ValidationError('Unauthorized');
  }
  if (stateCookie !== parsedState.uniqueKey) {
    console.warn(`${ATHENA_OAUTH_STATE_COOKIE} cookie doesn't match state value`
      + `(${stateCookie[ATHENA_OAUTH_STATE_COOKIE]} vs ${parsedState.uniqueKey})`);
    return false;
  }
  return true;
};

export const setTokens = ({ accessToken, idToken }) => {
  const loginContainer = document.getElementById(LOGIN_CONTAINER_ID);
  loginContainer.setAttribute('data-id-token', idToken);
  loginContainer.setAttribute('data-access-token', accessToken);
};

